<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { debounce } from '@chatwoot/utils';
import DatePicker from 'vue-datepicker-next';
import { useTasksStore } from 'dashboard/stores/tasks';
import ContactAPI from 'dashboard/api/contacts';
import CompanyAPI from 'dashboard/api/companies';
import DealsAPI from 'dashboard/api/dealsApi';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TaskRecurrenceField from 'dashboard/components-next/Tasks/TaskRecurrenceField.vue';
import TaskEndTimeField from 'dashboard/components-next/Tasks/TaskEndTimeField.vue';
import {
  getTaskDueDate,
  getTaskEndDate,
  buildEndDate,
  isEndAfterStart,
  suggestEndTime,
} from 'dashboard/helper/taskRecurrence';

const props = defineProps({
  taskData: { type: Object, default: null },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();
const store = useStore();
const tasksStore = useTasksStore();

const LINK_TYPES = ['contact', 'company', 'deal'];

const form = ref({
  title: '',
  description: '',
  assignedToId: null,
  dueDate: null,
  endTime: null,
  allDay: false,
  recurrenceRule: null,
  linkType: 'contact',
  contactId: null,
  companyId: null,
  dealId: null,
});

const validationRules = {
  title: { required },
  assignedToId: { required },
  dueDate: { required },
  endTime: {
    required: () => form.value.allDay || !!form.value.endTime,
    afterStart: () =>
      form.value.allDay ||
      isEndAfterStart(form.value.dueDate, form.value.endTime),
  },
};

const v$ = useVuelidate(validationRules, form);

const titleHasError = computed(() => v$.value.title.$error);
const assignedToHasError = computed(() => v$.value.assignedToId.$error);
const dueDateHasError = computed(() => v$.value.dueDate.$error);
const endTimeHasError = computed(() => v$.value.endTime.$error);
const agentOptions = computed(() =>
  (store.getters['agents/getAgents'] || []).map(a => ({
    value: a.id,
    label: a.name,
  }))
);

const contactOptions = ref([]);
const companyOptions = ref([]);
const dealOptions = ref([]);

const linkTypeOptions = LINK_TYPES.map(value => ({
  value,
  label: t(`TASKS.CREATE.FIELDS.LINK_TYPE_${value.toUpperCase()}`),
}));

const linkValueOptions = computed(() => {
  if (form.value.linkType === 'contact') return contactOptions.value;
  if (form.value.linkType === 'company') return companyOptions.value;
  return dealOptions.value;
});

const linkValue = computed({
  get: () => {
    if (form.value.linkType === 'contact') return form.value.contactId;
    if (form.value.linkType === 'company') return form.value.companyId;
    return form.value.dealId;
  },
  set: value => {
    if (form.value.linkType === 'contact') form.value.contactId = value;
    else if (form.value.linkType === 'company') form.value.companyId = value;
    else form.value.dealId = value;
  },
});

const contactToOption = c => ({
  value: c.id,
  label: c.name || c.email || String(c.id),
});
const companyToOption = c => ({ value: c.id, label: c.name || String(c.id) });
const dealToOption = d => ({ value: d.id, label: d.name || String(d.id) });

const loadContacts = async () => {
  const {
    data: { payload },
  } = await ContactAPI.get(1, 'name');
  contactOptions.value = payload.map(contactToOption);
};

const loadCompanies = async () => {
  const {
    data: { payload },
  } = await CompanyAPI.get({ page: 1, sort: 'name' });
  companyOptions.value = payload.map(companyToOption);
};

const loadDeals = async () => {
  const {
    data: { payload },
  } = await DealsAPI.get({ page: 1 });
  dealOptions.value = payload.map(dealToOption);
};

const searchContacts = debounce(async query => {
  if (!query) {
    await loadContacts();
    return;
  }
  const {
    data: { payload },
  } = await ContactAPI.search(query, 1, 'name');
  contactOptions.value = payload.map(contactToOption);
}, 300);

const searchCompanies = debounce(async query => {
  if (!query) {
    await loadCompanies();
    return;
  }
  const {
    data: { payload },
  } = await CompanyAPI.search(query, 1, 'name');
  companyOptions.value = payload.map(companyToOption);
}, 300);

const searchDeals = debounce(async query => {
  await DealsAPI.get({ page: 1, search: query || undefined }).then(
    ({ data: { payload } }) => {
      dealOptions.value = payload.map(dealToOption);
    }
  );
}, 300);

const ensureOptionPresent = (options, id, label) => {
  if (!id || options.value.some(option => option.value === id)) return;
  options.value = [{ value: id, label: label || String(id) }, ...options.value];
};

const linkTypeForTask = task => {
  if (task.contactId) return 'contact';
  if (task.companyId) return 'company';
  return 'deal';
};

const syncForm = task => {
  if (!task) return;
  form.value = {
    title: task.title || '',
    description: task.description || '',
    assignedToId: task.assignedToId || null,
    dueDate: getTaskDueDate(task),
    endTime:
      getTaskEndDate(task) ??
      (task.allDay ? null : suggestEndTime(getTaskDueDate(task))),
    allDay: task.allDay,
    recurrenceRule: task.recurrenceRule || null,
    linkType: linkTypeForTask(task),
    contactId: task.contactId || null,
    companyId: task.companyId || null,
    dealId: task.dealId || null,
  };
  ensureOptionPresent(contactOptions, task.contactId, task.contact?.name);
  ensureOptionPresent(companyOptions, task.companyId, task.company?.name);
  ensureOptionPresent(dealOptions, task.dealId, task.deal?.name);
};

watch(() => props.taskData, syncForm, { immediate: true });

const loadOptions = async () => {
  await Promise.all([
    loadContacts(),
    loadCompanies(),
    loadDeals(),
    store.dispatch('agents/get'),
  ]);
  syncForm(props.taskData);
};

onMounted(loadOptions);

const resetValidation = () => {
  v$.value.$reset();
};

const save = async ({ scope, occurrenceDate } = {}) => {
  if (!props.taskData?.id) return null;
  v$.value.$touch();
  if (v$.value.$invalid) return null;
  const updated = await tasksStore.update({
    id: props.taskData.id,
    title: form.value.title.trim(),
    description: form.value.description || null,
    assigned_to_id: form.value.assignedToId,
    due_date: form.value.dueDate,
    end_date: form.value.allDay
      ? null
      : buildEndDate(form.value.dueDate, form.value.endTime),
    all_day: form.value.allDay,
    ...(props.taskData.recurringTaskId
      ? {}
      : { recurrence_rule: form.value.recurrenceRule }),
    scope,
    occurrenceDate,
    contact_id: form.value.contactId,
    company_id: form.value.companyId,
    deal_id: form.value.dealId,
  });
  emit('saved', updated);
  return updated;
};

const validate = () => {
  v$.value.$touch();
  return !v$.value.$invalid;
};

defineExpose({ save, loadOptions, validate, resetValidation });
</script>

<template>
  <div class="flex flex-col gap-4">
    <Input
      v-model="form.title"
      :label="t('TASKS.CREATE.FIELDS.TITLE')"
      :placeholder="t('TASKS.CREATE.FIELDS.TITLE_PLACEHOLDER')"
      :message="titleHasError ? t('TASKS.CREATE.FIELDS.TITLE_ERROR') : ''"
      :message-type="titleHasError ? 'error' : 'info'"
      custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
      @input="v$.title.$touch()"
      @blur="v$.title.$touch()"
    />

    <Input
      v-model="form.description"
      :label="t('TASKS.CREATE.FIELDS.DESCRIPTION')"
      :placeholder="t('TASKS.CREATE.FIELDS.DESCRIPTION_PLACEHOLDER')"
      custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
    />

    <div class="flex flex-col gap-1">
      <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
        t('TASKS.CREATE.FIELDS.ASSIGNED_TO')
      }}</span>
      <ComboBox
        v-model="form.assignedToId"
        :options="agentOptions"
        :placeholder="t('TASKS.CREATE.FIELDS.ASSIGNED_TO_PLACEHOLDER')"
        :has-error="assignedToHasError"
        :message="
          assignedToHasError ? t('TASKS.CREATE.FIELDS.ASSIGNED_TO_ERROR') : ''
        "
        @update:model-value="v$.assignedToId.$touch()"
      />
    </div>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div class="flex flex-col gap-1">
        <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
          t('TASKS.CREATE.FIELDS.DUE_DATE')
        }}</span>
        <DatePicker
          v-model:value="form.dueDate"
          :type="form.allDay ? 'date' : 'datetime'"
          :show-second="false"
          :placeholder="t('TASKS.CREATE.FIELDS.DUE_DATE_PLACEHOLDER')"
          input-class="mx-input"
          :append-to-body="false"
          @change="v$.dueDate.$touch()"
        />
        <span v-if="dueDateHasError" class="text-xs text-n-ruby-9">
          {{ t('TASKS.CREATE.FIELDS.DUE_DATE_ERROR') }}
        </span>
      </div>

      <TaskEndTimeField
        v-if="!form.allDay"
        v-model="form.endTime"
        :has-error="endTimeHasError"
        @update:model-value="v$.endTime.$touch()"
      />
    </div>

    <div class="flex items-center gap-2">
      <Switch v-model="form.allDay" />
      <span class="text-sm text-n-slate-12">{{
        t('TASKS.CREATE.FIELDS.ALL_DAY')
      }}</span>
    </div>

    <TaskRecurrenceField
      v-if="!taskData?.recurringTaskId"
      v-model="form.recurrenceRule"
      :due-date="form.dueDate"
    />

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div class="flex flex-col gap-1">
        <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
          t('TASKS.CREATE.FIELDS.LINK_TYPE')
        }}</span>
        <ComboBox v-model="form.linkType" :options="linkTypeOptions" />
      </div>

      <div class="flex flex-col gap-1">
        <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
          t('TASKS.CREATE.FIELDS.LINK_VALUE')
        }}</span>
        <ComboBox
          v-model="linkValue"
          :options="linkValueOptions"
          :placeholder="t('TASKS.CREATE.FIELDS.LINK_VALUE_PLACEHOLDER')"
          :search-placeholder="t('TASKS.CREATE.FIELDS.LINK_VALUE_PLACEHOLDER')"
          @search="
            form.linkType === 'contact'
              ? searchContacts($event)
              : form.linkType === 'company'
                ? searchCompanies($event)
                : searchDeals($event)
          "
        />
      </div>
    </div>
  </div>
</template>
